import {
  ArrowLeftOutlined,
  CloseOutlined,
  SafetyCertificateOutlined,
} from "@ant-design/icons";
import {
  type AIConnector,
  getConnectorDisplayName,
} from "@p0security/shared/types/ai-inventory";
import { Button, Space, Tag, Typography } from "antd";
import { useCallback } from "react";
import { color, radius } from "theme/aiInventory";

import type { ConnectorWithContext } from "../graph/types.ts";
import { CopilotStudioConnectorDetails } from "../platforms/CopilotStudioConnectorDetails.tsx";
import { isConnectorManaged } from "../platforms/enrollment.ts";

const renderDetails = (connector: AIConnector): React.ReactNode => {
  switch (connector.platform) {
    case "copilot-studio":
      return <CopilotStudioConnectorDetails tool={connector.data} />;
    case "local-agents":
      return (
        <Typography.Text type="secondary">
          MCP server: {connector.data.label}
        </Typography.Text>
      );
  }
};

export type ConnectorSidecarProps = {
  connector: AIConnector | undefined;
  context?: ConnectorWithContext;
  onClose: () => void;
  onBack: () => void;
  onSecure?: (connector: AIConnector) => void;
};

export const ConnectorSidecar: React.FC<ConnectorSidecarProps> = ({
  connector,
  context,
  onClose,
  onBack,
  onSecure,
}) => {
  const managed = connector
    ? (context?.managed ?? isConnectorManaged(connector))
    : false;
  const handleSecure = useCallback(() => {
    if (connector) onSecure?.(connector);
  }, [connector, onSecure]);

  if (!connector) return null;

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={onBack}>
          Back
        </Button>
        <Button type="text" icon={<CloseOutlined />} onClick={onClose} />
      </Space>

      <Space size={8}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          {getConnectorDisplayName(connector)}
        </Typography.Title>
        {managed ? (
          <Tag color="green">Managed</Tag>
        ) : (
          <Tag color="orange">Unmanaged</Tag>
        )}
      </Space>

      {renderDetails(connector)}

      {!managed && (
        <div
          style={{
            border: `1.5px dashed ${color.unmanaged}`,
            borderRadius: radius.card,
            padding: 16,
            background: color.unmanagedBg,
          }}
        >
          <Typography.Paragraph style={{ marginBottom: 12 }}>
            Route this connector through the P0 Gateway to enforce policies and
            record sessions.
          </Typography.Paragraph>
          <Button
            type="primary"
            size="large"
            icon={<SafetyCertificateOutlined />}
            onClick={handleSecure}
            block
          >
            Secure with P0
          </Button>
        </div>
      )}
    </Space>
  );
};
